/**
 * Policies.
 *
 * The page leads with the organisation's own policies, because that is what a
 * returning user came here for. Templates are how a policy gets CREATED, which
 * is an action, so they live behind the "Create policy" button and only load
 * when it is pressed.
 *
 * LANGUAGE
 * A policy is written in one language, shown as a short tag (EN, EL) beside its
 * name. An organisation can hold the same policy in several languages as
 * separate, individually authoritative documents. Creating a policy includes
 * choosing its language, defaulting to the organisation's policy language.
 *
 * One rule shapes the create flow and is worth stating here: a policy generated
 * from a template is written in the TEMPLATE's language, whatever language the
 * user asked for. The system library is English only, so a user who asks for
 * Greek is offered the English templates, told plainly that the result will be
 * English, and the resulting policy is labelled EN. The alternative — stamping
 * "Greek" on English text — would produce a document whose stated language is
 * wrong, and nobody re-checks a document they think they already know. If a
 * team translates one, the translation is a separate policy in its own language.
 *
 * Version history is shown inline on a policy, because "which version was in
 * force in March" is the question an auditor actually asks.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '@opencomply/shared';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApiError } from '../lib/useApiError';
import { cn } from '../lib/cn';
import {
  Card, Button, Pill, Field, EmptyState, Skeleton, ErrorNotice, TONE_FOR_STATUS,
} from '../components/ui';
import { IconPolicy } from '../components/icons';
import { LanguageTag, useLanguageName } from '../components/LanguageTag';
import { LicenseAttribution } from '../components/LicenseAttribution';

// ---------------------------------------------------------------------------
//  Strings (English defaults; keys are the i18n keys)
//
//  `{{x}}` is i18next interpolation. `_one` / `_other` pairs are plural forms of
//  the key without the suffix. Keys that already exist in the bundled
//  translations (policies.title, policies.subtitle, policies.none,
//  policies.version, policies.versions, policies.documentCode,
//  policies.systemTemplate, policies.customTemplate, policies.createdBy,
//  policies.generate, common.*) are reused directly and not repeated here.
// ---------------------------------------------------------------------------

export const POLICIES_PAGE_STRINGS = {
  // Page
  'policies.create': 'Create policy',
  'policies.noneHint': 'Create your first policy from a template, or start with a blank one.',
  'policies.noMatch': 'No policies match these filters.',
  'policies.clearFilters': 'Clear filters',
  'policies.resultCount_one': '{{count}} policy',
  'policies.resultCount_other': '{{count}} policies',
  'policies.filterLanguage': 'Language',
  'policies.allLanguages': 'All languages',
  'policies.linkedLanguages': 'Available languages — open one to read or review it',
  'policies.openLanguage': 'Open the {{name}} version',
  'policies.languageOption': '{{name}} ({{count}})',
  'policies.listLoadFailed': 'Could not load your policies.',
  // Create panel
  'policies.createTitle': 'Create a policy',
  'policies.createClose': 'Close',
  'policies.stepChoose': 'Step 1 of 2: choose a starting point',
  'policies.stepDetails': 'Step 2 of 2: name your policy',
  'policies.policyLanguage': 'Policy language',
  'policies.policyLanguageHint': 'The language the policy is written in. Defaults to your organisation’s policy language.',
  'policies.startBlank': 'Start from a blank policy in {{language}}',
  'policies.startBlankHint': 'An empty policy you write yourself. Use this for a language no template exists in yet.',
  'policies.useBlank': 'Use a blank policy',
  'policies.orTemplate': 'or start from a template',
  'policies.searchTemplates': 'Search templates',
  'policies.searchTemplatesPlaceholder': 'Search by name or topic…',
  'policies.filterFramework': 'Framework',
  'policies.allFrameworks': 'All frameworks',
  'policies.templateCount_one': '{{count}} template',
  'policies.templateCount_other': '{{count}} templates',
  'policies.templatesLoading': 'Loading templates…',
  'policies.templatesFailed': 'Could not load the templates.',
  'policies.noTemplates': 'No templates match your search.',
  'policies.noTemplatesInLanguage': 'There are no templates in {{language}} yet. Showing all templates; each is marked with its own language, and the policy you create is written in that language.',
  'policies.includeOtherLanguages': 'Also show templates in other languages',
  'policies.useTemplate': 'Use this template',
  'policies.back': 'Back',
  // Details step
  'policies.fromTemplate': 'From template “{{name}}”',
  'policies.blankPolicy': 'Blank policy',
  'policies.policyName': 'Policy name',
  'policies.firstVersionLabel': 'Version',
  'policies.firstVersionHint': '3 to 20 characters, e.g. 1.0.',
  'policies.templateLanguageNote': 'This template is written in {{language}}, so the policy will be too. If you translate it, create the translation as a separate policy in its own language.',
  'policies.templateLanguageMismatch': 'You chose {{requested}}, but this template only exists in {{actual}}. The policy will be created in {{actual}} and labelled {{code}}.',
  'policies.createSubmit': 'Create policy',
  'policies.creating': 'Creating…',
  // Result notices
  'policies.createdNotice': '“{{name}}” was created in {{language}} ({{code}}).',
  'policies.createdMismatchNotice': '“{{name}}” was created in {{actual}}, not {{requested}}: that template is not available in {{requested}}. It is labelled {{code}} so it cannot be mistaken for a {{requested}} document.',
  'policies.openCreated': 'Open',
  'policies.dismiss': 'Dismiss',
} as const;

type PKey = keyof typeof POLICIES_PAGE_STRINGS;

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

interface Template {
  id: string; name: string; description: string; language: string;
  isSystem: boolean; frameworks: Array<{ key: string; name: string }>;
  /** Third-party source/licence. Null/false for our own templates; see LicenseAttribution. */
  license: string | null; sourceUrl: string | null; sourceAttribution: string | null;
  modifiedFromSource: boolean;
}
interface PolicyVersion {
  id: string; version: string; createdAt: string; createdByName: string | null;
  approval: { status: string; approver: { firstName: string; lastName: string } | null } | null;
}
/** One language version of a logical policy, used for the linked-language chips. */
interface TranslationSummary {
  id: string; language: string; name: string;
  version: string | null; approvalStatus: string | null; isActive: boolean; isCurrent: boolean;
}
interface Policy {
  id: string; name: string; description: string | null; documentCode: string | null;
  language: string;
  isActive: boolean; updatedAt: string; versions: PolicyVersion[];
  /** The group's language versions. Length 1 for a standalone policy. */
  translations: TranslationSummary[];
}
interface PolicyList {
  items: Policy[];
  total: number;
  /** Languages the organisation holds policies in, with counts. */
  languages: Array<{ language: string; count: number }>;
  /** Default for new policies. A default, not a constraint. */
  organizationLanguage: string;
}

type Source = { kind: 'blank' } | { kind: 'template'; template: Template };

interface Notice { tone: 'good' | 'warning'; text: string; policyId: string }

const DEFAULT_VERSION = '1.0';

export function PoliciesPage() {
  const { t, i18n } = useTranslation();
  const { allows } = useAuth();
  const queryClient = useQueryClient();
  const describeError = useApiError();
  const languageName = useLanguageName();

  /** Translate with the built-in English as the fallback. */
  const tx = (key: PKey, options?: Record<string, unknown>) =>
    t(key, { defaultValue: POLICIES_PAGE_STRINGS[key], ...options });
  /** Plural form: `base` has `_one` and `_other` defaults. */
  const tp = (base: 'policies.resultCount' | 'policies.templateCount', count: number) =>
    t(base, {
      count,
      defaultValue_one: POLICIES_PAGE_STRINGS[`${base}_one`],
      defaultValue_other: POLICIES_PAGE_STRINGS[`${base}_other`],
    });

  // --- List state -----------------------------------------------------------
  const [search, setSearch] = useState('');
  const [languageFilter, setLanguageFilter] = useState('');
  const [notice, setNotice] = useState<Notice | null>(null);

  // --- Create-panel state ---------------------------------------------------
  const [creating, setCreating] = useState(false);
  const [source, setSource] = useState<Source | null>(null);
  const [chosenLanguage, setChosenLanguage] = useState<string | null>(null);
  const [templateSearch, setTemplateSearch] = useState('');
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [otherLanguages, setOtherLanguages] = useState(false);
  const [policyName, setPolicyName] = useState('');
  const [documentCode, setDocumentCode] = useState('');
  const [version, setVersion] = useState(DEFAULT_VERSION);
  const [error, setError] = useState<string | null>(null);

  const { data: policies, isLoading, isError, refetch } = useQuery({
    queryKey: ['policies', { search, language: languageFilter }],
    queryFn: () =>
      api.get<PolicyList>(`/policies${qs({ search, language: languageFilter, pageSize: 100 })}`),
    // Keep the previous list on screen while a filter reloads, so the language
    // control does not vanish (and lose focus) between keystrokes.
    placeholderData: (previous) => previous,
  });

  const orgLanguage = policies?.organizationLanguage ?? 'en';
  // The language the user is creating in: their pick, else the organisation's.
  const language = chosenLanguage ?? orgLanguage;

  const { data: templates, isLoading: templatesLoading, isError: templatesError } = useQuery({
    queryKey: ['policy-templates'],
    // Only fetched once someone opens the create panel: a returning user came
    // for their own policies and should not wait on a 111-row library.
    enabled: creating,
    // Fetch EVERY page, not just the first.
    //
    // The server caps pageSize at 100 and the library is larger than that, so a
    // single request silently hid the tail of the list — alphabetically, the
    // last templates simply did not exist as far as the customer could tell.
    // A truncated list with no indication it is truncated is worse than an
    // error: nobody goes looking for a policy they have no reason to think is
    // there.
    queryFn: async () => {
      const PAGE = 100;
      const first = await api.get<{ items: Template[]; total: number }>(
        `/policies/templates?pageSize=${PAGE}&page=1`,
      );
      const items = [...first.items];
      const pages = Math.ceil((first.total ?? items.length) / PAGE);
      for (let page = 2; page <= pages; page++) {
        const next = await api.get<{ items: Template[] }>(
          `/policies/templates?pageSize=${PAGE}&page=${page}`,
        );
        items.push(...next.items);
      }
      return { items, total: first.total ?? items.length };
    },
  });

  // --- Template browsing: language, then framework, then text ---------------
  const allTemplates = templates?.items ?? [];
  const inLanguage = useMemo(
    () => allTemplates.filter((tpl) => tpl.language === language),
    [allTemplates, language],
  );
  // Never leave the customer with an empty library: when nothing exists in the
  // chosen language, fall back to everything — and say so.
  const noneInLanguage = allTemplates.length > 0 && inLanguage.length === 0;
  const showAll = noneInLanguage || otherLanguages;
  const pool = showAll ? allTemplates : inLanguage;

  const frameworkOptions = useMemo(() => {
    const byKey = new Map<string, string>();
    for (const tpl of pool) for (const fw of tpl.frameworks) byKey.set(fw.key, fw.name);
    return [...byKey.entries()].sort((a, b) => a[1].localeCompare(b[1], i18n.language));
  }, [pool, i18n.language]);

  const visibleTemplates = useMemo(() => {
    const needle = templateSearch.trim().toLocaleLowerCase(i18n.language);
    return pool.filter((tpl) => {
      if (frameworkFilter && !tpl.frameworks.some((fw) => fw.key === frameworkFilter)) return false;
      if (!needle) return true;
      return (
        tpl.name.toLocaleLowerCase(i18n.language).includes(needle) ||
        tpl.description.toLocaleLowerCase(i18n.language).includes(needle)
      );
    });
  }, [pool, frameworkFilter, templateSearch, i18n.language]);

  // --- Mutation -------------------------------------------------------------
  const create = useMutation({
    mutationFn: async () => {
      const common = {
        name: policyName.trim(),
        ...(documentCode.trim() ? { documentCode: documentCode.trim() } : {}),
        ...(version.trim() ? { version: version.trim() } : {}),
      };
      // No `language` for a template: the server derives it from the template
      // and rejects a body that tries to say otherwise.
      return source!.kind === 'template'
        ? api.post<Policy>('/policies/generate', { templateId: source!.template.id, ...common })
        : api.post<Policy>('/policies', { ...common, language });
    },
    onSuccess: (policy) => {
      const requested = language;
      const mismatch = source?.kind === 'template' && policy.language !== requested;
      const actualName = languageName(policy.language);
      setNotice({
        tone: mismatch ? 'warning' : 'good',
        policyId: policy.id,
        text: mismatch
          ? tx('policies.createdMismatchNotice', {
              name: policy.name, actual: actualName, requested: languageName(requested), code: policy.language.toUpperCase(),
            })
          : tx('policies.createdNotice', { name: policy.name, language: actualName, code: policy.language.toUpperCase() }),
      });
      closePanel();
      void queryClient.invalidateQueries({ queryKey: ['policies'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (err) => setError(describeError(err)),
  });

  function openPanel() {
    setNotice(null);
    setError(null);
    setCreating(true);
  }
  function closePanel() {
    setCreating(false);
    setSource(null);
    setChosenLanguage(null);
    setTemplateSearch('');
    setFrameworkFilter('');
    setOtherLanguages(false);
    setPolicyName('');
    setDocumentCode('');
    setVersion(DEFAULT_VERSION);
    setError(null);
  }
  function choose(next: Source) {
    setError(null);
    setSource(next);
    setPolicyName(next.kind === 'template' ? next.template.name : '');
  }

  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' }).format(new Date(iso));

  const canCreate = allows('policy.create');
  const filtersActive = search.trim() !== '' || languageFilter !== '';
  const hasPolicies = (policies?.total ?? 0) > 0 || filtersActive;

  const versionOk = version.trim().length >= 3 && version.trim().length <= 20;
  const nameOk = policyName.trim().length > 0;

  // Languages for the list filter: those in use, plus the current selection so
  // the control never shows a value it has no option for.
  const filterLanguages = useMemo(() => {
    const list = [...(policies?.languages ?? [])];
    if (languageFilter && !list.some((l) => l.language === languageFilter)) {
      list.push({ language: languageFilter, count: 0 });
    }
    return list;
  }, [policies?.languages, languageFilter]);

  const createButton = canCreate && (
    <Button variant="primary" onClick={openPanel} disabled={creating}>
      {tx('policies.create')}
    </Button>
  );

  return (
    <div className="oc-page space-y-5 animate-fade-in">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="oc-h1">{t('policies.title')}</h1>
          <p className="oc-muted mt-1">{t('policies.subtitle')}</p>
        </div>
        {createButton}
      </header>

      {notice && (
        <div
          role="status"
          className={cn(
            'rounded-xl border px-4 py-3 text-sm text-ink flex flex-wrap items-start justify-between gap-x-3 gap-y-2',
            notice.tone === 'warning' ? 'border-warning/40 bg-warning/10' : 'border-good/30 bg-good/[0.07]',
          )}
        >
          <span className="min-w-0 flex-1">{notice.text}</span>
          <span className="flex items-center gap-3 shrink-0">
            <Link to={`/policies/${notice.policyId}`} className="text-xs font-medium text-brand hover:underline">
              {tx('policies.openCreated')}
            </Link>
            <button className="text-xs font-medium text-brand hover:underline" onClick={() => setNotice(null)}>
              {tx('policies.dismiss')}
            </button>
          </span>
        </div>
      )}

      {/* ------------------------------------------------------------------
          Create panel. Closed by default; templates are fetched only once it
          opens. Sits above the list while open, and goes away when done.
          ------------------------------------------------------------------ */}
      {creating && canCreate && (
        <Card className="space-y-4" >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="oc-h2">{tx('policies.createTitle')}</h2>
              <p className="oc-muted mt-0.5">
                {source ? tx('policies.stepDetails') : tx('policies.stepChoose')}
              </p>
            </div>
            <button
              onClick={closePanel}
              aria-label={tx('policies.createClose')}
              className="p-2 -mr-2 -mt-1 rounded-lg text-ink-secondary hover:bg-surface-sunken"
            >
              <svg viewBox="0 0 20 20" className="w-5 h-5" fill="currentColor" aria-hidden>
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </button>
          </div>

          {error && <ErrorNotice message={error} />}

          {!source ? (
            <>
              <Field label={tx('policies.policyLanguage')} htmlFor="create-language"
                     hint={tx('policies.policyLanguageHint')}>
                <select
                  id="create-language"
                  className="oc-input sm:max-w-xs"
                  value={language}
                  onChange={(e) => { setChosenLanguage(e.target.value); setOtherLanguages(false); }}
                >
                  {LANGUAGES.map((code) => (
                    <option key={code} value={code}>{languageName(code)} ({code.toUpperCase()})</option>
                  ))}
                </select>
              </Field>

              <div className="rounded-xl border border-line p-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0 flex-1 basis-60">
                  <p className="text-sm font-medium text-ink">
                    {tx('policies.startBlank', { language: languageName(language) })}
                  </p>
                  <p className="text-xs text-ink-secondary mt-0.5">{tx('policies.startBlankHint')}</p>
                </div>
                <Button onClick={() => choose({ kind: 'blank' })}>{tx('policies.useBlank')}</Button>
              </div>

              <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted pt-1">
                {tx('policies.orTemplate')}
              </p>

              {templatesLoading ? (
                <div className="space-y-2" aria-label={tx('policies.templatesLoading')}>
                  {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}
                </div>
              ) : templatesError ? (
                <ErrorNotice message={tx('policies.templatesFailed')} />
              ) : (
                <>
                  {noneInLanguage && (
                    <div role="status" className="rounded-xl border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm text-ink">
                      {tx('policies.noTemplatesInLanguage', { language: languageName(language) })}
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
                    <div>
                      <label className="oc-label" htmlFor="template-search">{tx('policies.searchTemplates')}</label>
                      <input
                        id="template-search"
                        type="search"
                        className="oc-input"
                        placeholder={tx('policies.searchTemplatesPlaceholder')}
                        value={templateSearch}
                        onChange={(e) => setTemplateSearch(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="oc-label" htmlFor="template-framework">{tx('policies.filterFramework')}</label>
                      <select
                        id="template-framework"
                        className="oc-input"
                        value={frameworkFilter}
                        onChange={(e) => setFrameworkFilter(e.target.value)}
                      >
                        <option value="">{tx('policies.allFrameworks')}</option>
                        {frameworkOptions.map(([key, name]) => (
                          <option key={key} value={key}>{name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
                    <p className="text-xs text-ink-muted" aria-live="polite">{tp('policies.templateCount', visibleTemplates.length)}</p>
                    {!noneInLanguage && allTemplates.length > inLanguage.length && (
                      <label className="flex items-center gap-2 text-xs text-ink-secondary min-h-[2.25rem]">
                        <input type="checkbox" checked={otherLanguages} onChange={(e) => setOtherLanguages(e.target.checked)} />
                        {tx('policies.includeOtherLanguages')}
                      </label>
                    )}
                  </div>

                  {visibleTemplates.length === 0 ? (
                    <p className="text-sm text-ink-secondary py-4 text-center">{tx('policies.noTemplates')}</p>
                  ) : (
                    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 sm:max-h-[28rem] sm:overflow-y-auto sm:pr-1">
                      {visibleTemplates.map((template) => (
                        <li key={template.id} className="rounded-xl border border-line p-3 flex flex-col">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="text-sm font-medium text-ink leading-snug min-w-0">{template.name}</p>
                            <span className="flex flex-wrap items-center justify-end gap-1 shrink-0 max-w-[65%]">
                              <LanguageTag code={template.language} />
                              <LicenseAttribution
                                variant="badge"
                                license={template.license}
                                sourceUrl={template.sourceUrl}
                                modifiedFromSource={template.modifiedFromSource}
                              />
                              <span className="oc-pill-neutral">
                                {template.isSystem ? t('policies.systemTemplate') : t('policies.customTemplate')}
                              </span>
                            </span>
                          </div>
                          <p className="text-xs text-ink-secondary leading-relaxed flex-1">{template.description}</p>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {template.frameworks.map((fw) => (
                              <span key={fw.key} className="text-[10px] px-1.5 py-0.5 rounded bg-brand/10 text-brand font-medium">
                                {fw.name}
                              </span>
                            ))}
                          </div>
                          <Button size="sm" className="w-full mt-3" onClick={() => choose({ kind: 'template', template })}>
                            {tx('policies.useTemplate')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </>
          ) : (
            <form
              className="max-w-xl"
              onSubmit={(e) => { e.preventDefault(); if (nameOk && versionOk && !create.isPending) create.mutate(); }}
            >
              <p className="text-sm text-ink-secondary mb-4">
                {source.kind === 'template'
                  ? tx('policies.fromTemplate', { name: source.template.name })
                  : tx('policies.blankPolicy')}
              </p>

              <Field label={tx('policies.policyName')} htmlFor="policy-name" required>
                <input id="policy-name" className="oc-input" value={policyName} maxLength={255}
                       onChange={(e) => setPolicyName(e.target.value)} />
              </Field>
              <div className="grid gap-x-4 sm:grid-cols-2">
                <Field label={t('policies.documentCode')} htmlFor="doc-code" hint={t('common.optional')}>
                  <input id="doc-code" className="oc-input font-mono" value={documentCode} maxLength={50}
                         onChange={(e) => setDocumentCode(e.target.value)} placeholder="POL-ISMS-001" />
                </Field>
                <Field label={tx('policies.firstVersionLabel')} htmlFor="policy-version" hint={tx('policies.firstVersionHint')}
                       error={versionOk ? undefined : tx('policies.firstVersionHint')}>
                  <input id="policy-version" className="oc-input font-mono" value={version} maxLength={20}
                         onChange={(e) => setVersion(e.target.value)} />
                </Field>
              </div>

              {source.kind === 'blank' ? (
                <Field label={tx('policies.policyLanguage')} htmlFor="policy-language"
                       hint={tx('policies.policyLanguageHint')}>
                  <select id="policy-language" className="oc-input sm:max-w-xs" value={language}
                          onChange={(e) => setChosenLanguage(e.target.value)}>
                    {LANGUAGES.map((code) => (
                      <option key={code} value={code}>{languageName(code)} ({code.toUpperCase()})</option>
                    ))}
                  </select>
                </Field>
              ) : (
                <div className="mb-4">
                  <span className="oc-label">{tx('policies.policyLanguage')}</span>
                  <p className="flex items-center gap-2 text-sm text-ink min-h-[2.5rem]">
                    <LanguageTag code={source.template.language} />
                    {languageName(source.template.language)}
                  </p>
                  {source.template.language !== language ? (
                    <div role="status" className="mt-1 rounded-xl border border-warning/40 bg-warning/10 px-3 py-2.5 text-sm text-ink">
                      {tx('policies.templateLanguageMismatch', {
                        requested: languageName(language),
                        actual: languageName(source.template.language),
                        code: source.template.language.toUpperCase(),
                      })}
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-ink-muted">
                      {tx('policies.templateLanguageNote', { language: languageName(source.template.language) })}
                    </p>
                  )}
                </div>
              )}

              {/* Source and licence of an adapted template. Renders nothing for our own
                  templates. The same notice is inside the document body, so the generated
                  policy keeps it; this is the structured, visible copy. */}
              {source.kind === 'template' && (
                <LicenseAttribution
                  variant="panel"
                  className="mb-4"
                  license={source.template.license}
                  sourceUrl={source.template.sourceUrl}
                  sourceAttribution={source.template.sourceAttribution}
                  modifiedFromSource={source.template.modifiedFromSource}
                />
              )}

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                <Button onClick={() => { setSource(null); setError(null); }}>{tx('policies.back')}</Button>
                <Button variant="primary" type="submit" disabled={!nameOk || !versionOk || create.isPending}>
                  {create.isPending ? tx('policies.creating') : tx('policies.createSubmit')}
                </Button>
              </div>
            </form>
          )}
        </Card>
      )}

      {/* ------------------------------------------------------------------
          The organisation's own policies. The point of the page.
          ------------------------------------------------------------------ */}
      <Card>
        {isLoading ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16" />)}</div>
        ) : isError ? (
          <ErrorNotice message={tx('policies.listLoadFailed')} onRetry={() => void refetch()} />
        ) : (
          <>
            {hasPolicies && (
              <div className="flex flex-wrap items-end gap-3 mb-4">
                <div className="flex-1 basis-48 min-w-0">
                  <label className="oc-label" htmlFor="policy-search">{t('common.search')}</label>
                  <input
                    id="policy-search"
                    className="oc-input"
                    placeholder={t('common.search')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    type="search"
                  />
                </div>
                <div className="basis-44 grow sm:grow-0">
                  <label className="oc-label" htmlFor="policy-language-filter">{tx('policies.filterLanguage')}</label>
                  <select
                    id="policy-language-filter"
                    className="oc-input"
                    value={languageFilter}
                    onChange={(e) => setLanguageFilter(e.target.value)}
                  >
                    <option value="">{tx('policies.allLanguages')}</option>
                    {filterLanguages.map((l) => (
                      <option key={l.language} value={l.language}>
                        {tx('policies.languageOption', { name: `${languageName(l.language)} · ${l.language.toUpperCase()}`, count: l.count })}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="text-xs text-ink-muted pb-3 w-full sm:w-auto sm:ml-auto" aria-live="polite">
                  {tp('policies.resultCount', policies?.total ?? 0)}
                </p>
              </div>
            )}

            {!policies || policies.items.length === 0 ? (
              filtersActive ? (
                <EmptyState
                  icon={<IconPolicy className="w-10 h-10" />}
                  title={tx('policies.noMatch')}
                  action={
                    <Button onClick={() => { setSearch(''); setLanguageFilter(''); }}>
                      {tx('policies.clearFilters')}
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<IconPolicy className="w-10 h-10" />}
                  title={t('policies.none')}
                  description={canCreate ? tx('policies.noneHint') : undefined}
                  action={canCreate && !creating ? createButton : undefined}
                />
              )
            ) : (
              <ul className="divide-y divide-line -mx-1">
                {policies.items.map((policy) => {
                  const latest = policy.versions[0];
                  return (
                    <li key={policy.id} className="py-3.5 px-1 first:pt-0 last:pb-0">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          {/* The name is the link: the whole reading/editing/approval
                              workflow lives on the editor page, one tap away. The
                              language tag sits right beside it, where it cannot be missed. */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <Link to={`/policies/${policy.id}`} className="font-medium text-ink hover:text-brand hover:underline break-words">
                              {policy.name}
                            </Link>
                            {/* Linked language chips: one logical policy, each chip
                                opening that language's version. A standalone policy
                                has exactly one chip. */}
                            <span className="flex flex-wrap items-center gap-1" aria-label={tx('policies.linkedLanguages')}>
                              {(policy.translations.length ? policy.translations
                                : [{ id: policy.id, language: policy.language, isCurrent: true } as TranslationSummary]
                              ).map((tr) => (
                                <Link
                                  key={tr.id}
                                  to={`/policies/${tr.id}`}
                                  title={tx('policies.openLanguage', { name: languageName(tr.language) })}
                                  aria-label={tx('policies.openLanguage', { name: languageName(tr.language) })}
                                  className="rounded hover:ring-2 hover:ring-brand/40 focus:outline-none focus:ring-2 focus:ring-brand/60"
                                >
                                  <LanguageTag code={tr.language} />
                                </Link>
                              ))}
                            </span>
                          </div>
                          <p className="text-xs text-ink-muted mt-0.5">
                            {policy.documentCode && <span className="font-mono mr-2">{policy.documentCode}</span>}
                            {latest && `${t('policies.version')} ${latest.version} · ${formatDate(latest.createdAt)}`}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                          <Link to={`/policies/${policy.id}`} className="oc-btn-secondary oc-btn-sm">
                            {t('policies.openEditor', { defaultValue: 'Open' })}
                          </Link>
                          <Pill tone={policy.isActive ? 'good' : 'neutral'}>
                            {policy.isActive ? t('status.APPROVED') : t('status.DRAFT')}
                          </Pill>
                          {latest?.approval && (
                            <Pill tone={TONE_FOR_STATUS[latest.approval.status] ?? 'neutral'}>
                              {t(`status.${latest.approval.status}`)}
                            </Pill>
                          )}
                        </div>
                      </div>
                      {policy.versions.length > 1 && (
                        <details className="mt-2">
                          <summary className="text-xs text-brand cursor-pointer hover:underline">
                            {t('policies.versions')} ({policy.versions.length})
                          </summary>
                          <ul className="mt-2 space-y-1 pl-3 border-l-2 border-line">
                            {policy.versions.map((v) => (
                              <li key={v.id} className="text-xs text-ink-secondary">
                                <span className="font-medium text-ink">{v.version}</span>
                                {' · '}{formatDate(v.createdAt)}
                                {v.createdByName && ` · ${t('policies.createdBy', { name: v.createdByName })}`}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
